import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import StippledBackground from '@/components/StippledBackground'
import Button from '@/components/ui/Button'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import { api } from '@/lib/api/client'
import {
  applyThemeGlobally,
  theme as defaultTheme,
  resetThemeGlobally,
} from '@/lib/theme'

interface SharedThemeSnapshot {
  id: string
  name: string
  content: string
  type: 'theme'
  language: 'json'
  description?: string
  createdAt: string
  originalUserId: string
  viewCount: number
  importCount: number
}

interface MockWindowProps {
  title: string
  top: string
  left: string
  width: string
  height: string
  zIndex: number
  colors: any
}

function _MockWindow({
  title,
  top,
  left,
  width,
  height,
  zIndex,
  colors,
}: MockWindowProps) {
  return (
    <div
      style={{
        position: 'absolute',
        top,
        left,
        width,
        height,
        zIndex,
        backgroundColor: colors.primaryBg,
        border: `1px solid ${colors.primaryBorder}`,
        borderRadius: '10px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Window title bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: defaultTheme.spacing.sm,
          padding: `${defaultTheme.spacing.sm} ${defaultTheme.spacing.md}`,
          backgroundColor: colors.secondaryBg,
          borderBottom: `1px solid ${colors.primaryBorder}`,
        }}
      >
        <div style={{ display: 'flex', gap: '8px' }}>
          <div
            style={{
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: colors.windowControlBg,
              border: `1px solid ${colors.windowBorder}`,
            }}
          />
          <div
            style={{
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: colors.windowControlBg,
              border: `1px solid ${colors.windowBorder}`,
            }}
          />
          <div
            style={{
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: colors.windowControlBg,
              border: `1px solid ${colors.windowBorder}`,
            }}
          />
        </div>
        <div
          style={{
            fontSize: defaultTheme.fontSize.sm,
            color: colors.primaryText,
            fontWeight: '500',
          }}
        >
          {title}
        </div>
      </div>
      {/* Window content */}
      <div
        style={{
          flex: 1,
          padding: defaultTheme.spacing.md,
          overflow: 'hidden',
        }}
      >
        {title === 'Terminal' && (
          <div
            style={{
              fontFamily: defaultTheme.fontFamily.mono,
              fontSize: defaultTheme.fontSize.sm,
              color: colors.primaryText,
            }}
          >
            <div style={{ color: colors.primaryAccent }}>
              {'> '}
              <span style={{ color: colors.primaryText }}>kyogre status</span>
            </div>
            <div
              style={{
                marginTop: defaultTheme.spacing.xs,
                color: colors.secondaryText,
              }}
            >
              Connected to storage...
            </div>
            <div
              style={{
                marginTop: defaultTheme.spacing.xs,
                color: colors.successText,
              }}
            >
              ✓ All systems operational
            </div>
            <div
              style={{
                marginTop: defaultTheme.spacing.md,
                color: colors.primaryAccent,
              }}
            >
              {'> '}
              <span style={{ animation: 'blink 1s infinite' }}>_</span>
            </div>
          </div>
        )}
        {title === 'Code Editor' && (
          <div
            style={{
              fontFamily: defaultTheme.fontFamily.mono,
              fontSize: defaultTheme.fontSize.sm,
              lineHeight: '1.6',
            }}
          >
            <div style={{ color: colors.tertiaryText }}>
              <span style={{ color: colors.secondaryText }}>1</span>
              {'  '}
              <span style={{ color: colors.primaryAccent }}>function</span>{' '}
              <span style={{ color: colors.primaryText }}>greet</span>
              {'() {'}
            </div>
            <div style={{ color: colors.tertiaryText }}>
              <span style={{ color: colors.secondaryText }}>2</span>
              {'    '}
              <span style={{ color: colors.primaryAccent }}>return</span>{' '}
              <span style={{ color: colors.successText }}>
                "Hello, Kyogre!"
              </span>
            </div>
            <div style={{ color: colors.tertiaryText }}>
              <span style={{ color: colors.secondaryText }}>3</span>
              {'  }'}
            </div>
          </div>
        )}
        {title === 'Settings' && (
          <div style={{ fontSize: defaultTheme.fontSize.sm }}>
            <div
              style={{
                color: colors.primaryText,
                fontWeight: '600',
                marginBottom: defaultTheme.spacing.md,
              }}
            >
              Appearance
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: defaultTheme.spacing.sm,
                backgroundColor: colors.secondaryBg,
                borderRadius: defaultTheme.borderRadius.sm,
                marginBottom: defaultTheme.spacing.sm,
              }}
            >
              <span style={{ color: colors.secondaryText }}>Dark Mode</span>
              <div
                style={{
                  width: '40px',
                  height: '20px',
                  backgroundColor: colors.primaryAccent,
                  borderRadius: '10px',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    right: '2px',
                    top: '2px',
                    width: '16px',
                    height: '16px',
                    backgroundColor: colors.primaryBg,
                    borderRadius: '50%',
                  }}
                />
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: defaultTheme.spacing.sm,
                backgroundColor: colors.secondaryBg,
                borderRadius: defaultTheme.borderRadius.sm,
              }}
            >
              <span style={{ color: colors.secondaryText }}>Accent Color</span>
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: colors.primaryAccent,
                  borderRadius: defaultTheme.borderRadius.sm,
                  border: `1px solid ${colors.primaryBorder}`,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Simplified TopNav for shared theme page
interface SimpleTopNavProps {
  onThemeToggle: () => void
  onLoginClick: () => void
  isGuestMode: boolean
}

function SimpleTopNav({
  onThemeToggle,
  isGuestMode,
  onLoginClick,
}: SimpleTopNavProps) {
  const { isDark, colors } = useThemeMode()

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '40px',
        backgroundColor: colors.navBg,
        borderBottom: `1px solid ${colors.navBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 12px',
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        fontFamily: 'var(--font-departure-mono)',
      }}
    >
      {/* Logo */}
      <a
        href="https://honcho.chat"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          fontSize: '14px',
          fontWeight: 'bold',
          color: colors.navItemText,
          letterSpacing: '1px',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <svg width="20" height="18" viewBox="0 0 87 77" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-2px' }}>
          <path d="M55.29 0H38.77V6.89H55.29V0Z" fill="currentColor"/>
          <path d="M65.95 6.89H55.29V13.69H65.95V6.89Z" fill="currentColor"/>
          <path d="M72.66 13.69H65.95V20.4H72.66V13.69Z" fill="currentColor"/>
          <path d="M79.54 20.4H72.66V30.17H79.54V20.4Z" fill="currentColor"/>
          <path d="M86.33 30.16H79.54V46.54H86.33V30.16Z" fill="currentColor"/>
          <path d="M79.54 46.55H72.67V56.32H79.54V46.55Z" fill="currentColor"/>
          <path d="M72.67 56.31H65.95V63.11H72.67V56.31Z" fill="currentColor"/>
          <path d="M65.95 63.11H55.29V69.96H65.95V63.11Z" fill="currentColor"/>
          <path d="M55.29 69.96H35.84V76.65H55.29V69.96Z" fill="currentColor"/>
          <path d="M35.84 63.11H25.18V69.96H35.84V63.11Z" fill="currentColor"/>
          <path d="M25.18 56.31H18.39V63.11H25.18V56.31Z" fill="currentColor"/>
          <path d="M18.39 46.55H11.52V56.32H18.39V46.55Z" fill="currentColor"/>
          <path d="M4.87 33.97V20.4H0V40.8H4.87V46.55H11.52V33.97H4.87Z" fill="currentColor"/>
          <path d="M18.31 27.17H11.52V33.96H18.31V27.17Z" fill="currentColor"/>
          <path d="M38.77 6.89H18.39V13.69H38.77V6.89Z" fill="currentColor"/>
          <path d="M45.5 13.69H38.79V20.4H45.5V13.69Z" fill="currentColor"/>
          <path d="M18.32 27.17H38.79V20.4H18.39V13.69H4.87V20.4H18.32V27.17Z" fill="currentColor"/>
          <path d="M38.79 30.16H31.98V36.87H38.79V30.16Z" fill="currentColor"/>
          <path d="M59.09 30.16H52.28V36.87H59.09V30.16Z" fill="currentColor"/>
          <path d="M59.16 50.47H31.98V56.31H59.16V50.47Z" fill="currentColor"/>
        </svg>
        Honcho Chat
      </a>

      {/* Right side controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Login button for guest users */}
        {isGuestMode && (
          <button
            onClick={onLoginClick}
            style={{
              padding: '4px 12px',
              backgroundColor: colors.visualAccent1,
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.85'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            Sign in to Import
          </button>
        )}

        {/* Theme Toggle */}
        <button
          onClick={onThemeToggle}
          style={{
            padding: '4px 8px',
            background: 'transparent',
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: colors.primaryText,
            fontSize: '14px',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          {isDark ? '🌙' : '☀︎'}
        </button>
      </div>
    </div>
  )
}

// Message Bubble Component
interface MessageBubbleProps {
  children: React.ReactNode
  sender: 'user' | 'assistant'
  timestamp?: string
}

function MessageBubble({ children, sender, timestamp }: MessageBubbleProps) {
  const { colors } = useThemeMode()

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '8px',
        position: 'relative',
      }}
    >
      <div
        style={{
          padding: defaultTheme.spacing.md,
          backgroundColor:
            sender === 'user' ? colors.userMsgBgAlpha : colors.assistantMsgBg,
          borderRadius: '10px',
          borderLeft:
            sender === 'assistant'
              ? `3px solid ${colors.assistantMsgAccent}`
              : 'none',
          borderRight:
            sender === 'user' ? `3px solid ${colors.userMsgAccent}` : 'none',
          color: colors.primaryText,
          fontFamily: 'var(--font-departure-mono)',
          fontSize: '13px',
          lineHeight: '1.6',
        }}
      >
        {children}
      </div>
      {timestamp && (
        <div
          style={{
            fontSize: defaultTheme.fontSize.xs,
            color: colors.tertiaryText,
            marginTop: defaultTheme.spacing.xs,
            textAlign: 'right',
          }}
        >
          {timestamp}
        </div>
      )}
    </div>
  )
}

export default function SharedThemePage() {
  const { id } = useParams<{ id: string }>()
  const { colors, toggleTheme } = useThemeMode()
  const { user } = useUser()
  const navigate = useNavigate()
  const [themeSnapshot, setThemeSnapshot] =
    useState<SharedThemeSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [importSuccess, setImportSuccess] = useState(false)
  const [shouldApplyTheme, setShouldApplyTheme] = useState(false)
  const [themeApplied, setThemeApplied] = useState(false)
  const [showJson, setShowJson] = useState(false)

  useEffect(() => {
    if (!id) return

    async function fetchSharedTheme() {
      try {
        setLoading(true)
        const apiUrl = import.meta.env.VITE_API_URL
        const response = await fetch(`${apiUrl}/api/themes/shared/${id}`)

        if (!response.ok) {
          if (response.status === 404) {
            setError('Theme not found')
          } else {
            setError('Failed to load theme')
          }
          return
        }

        const data = await response.json()
        setThemeSnapshot(data)

        // Apply theme preview BEFORE rendering to prevent flicker
        try {
          const themeJson = JSON.parse(data.content)
          applyThemeGlobally(themeJson)
          setThemeApplied(true)

          // Small delay to ensure theme CSS variables are applied before render
          await new Promise(resolve => setTimeout(resolve, 50))
        } catch (parseError) {
          console.error('Error parsing theme JSON:', parseError)
          setError('Invalid theme format')
        }
      } catch (err) {
        console.error('Error fetching shared theme:', err)
        setError('Failed to load theme')
      } finally {
        setLoading(false)
      }
    }

    fetchSharedTheme()

    // Cleanup: reset theme when leaving page (unless user chose to apply it)
    return () => {
      if (themeApplied && !shouldApplyTheme) {
        resetThemeGlobally()
      }
    }
  }, [id])

  const handleImport = async (applyNow: boolean = false) => {
    if (!user) {
      // Redirect to login with return URL
      navigate(`/auth/login?redirect=/themes/shared/${id}`)
      return
    }

    setImporting(true)
    try {
      const { error: importError } = await api.api.themes.import({ id: id! }).post()

      if (importError) {
        console.error('Import error:', importError)
        setError('Failed to import theme')
        return
      }

      setImportSuccess(true)

      if (applyNow) {
        // Keep the theme applied after import (don't reset on cleanup)
        // The theme is already applied from the preview, so it will persist
        setShouldApplyTheme(true)
      }
      // If not applying now, user just wants to save it
      // cleanup will handle theme reset when they leave

      // Redirect to home after successful import
      setTimeout(() => {
        navigate('/')
      }, 2000)
    } catch (err) {
      console.error('Error importing theme:', err)
      setError('Failed to import theme')
    } finally {
      setImporting(false)
    }
  }

  const handleLoginClick = () => {
    navigate(`/auth/login?redirect=/themes/shared/${id}`)
  }

  if (loading) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav
          onThemeToggle={toggleTheme}
          isGuestMode={!user}
          onLoginClick={handleLoginClick}
        />
        <div
          style={{
            fontSize: defaultTheme.fontSize.lg,
            color: colors.primaryText,
            zIndex: 1,
          }}
        >
          Loading theme... ✨
        </div>
      </div>
    )
  }

  if (error || !themeSnapshot) {
    return (
      <div
        style={{
          position: 'relative',
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-departure-mono)',
        }}
      >
        <StippledBackground />
        <SimpleTopNav
          onThemeToggle={toggleTheme}
          isGuestMode={!user}
          onLoginClick={handleLoginClick}
        />
        <div
          style={{
            fontSize: defaultTheme.fontSize.lg,
            color: colors.primaryText,
            zIndex: 1,
          }}
        >
          {error || 'Theme not found'} 😢
        </div>
      </div>
    )
  }

  // Parse theme to show preview
  let themeData: any = {}
  try {
    themeData = JSON.parse(themeSnapshot.content)
  } catch (e) {
    console.error('Failed to parse theme:', e)
  }

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        fontFamily: 'var(--font-departure-mono)',
      }}
    >
      {/* Animated stars background */}
      <StippledBackground />

      {/* Top Navigation */}
      <SimpleTopNav
        onThemeToggle={toggleTheme}
        isGuestMode={!user}
        onLoginClick={handleLoginClick}
      />

      {/* Main Chat Window */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          paddingTop: '60px',
          paddingBottom: defaultTheme.spacing.xl,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          minHeight: '100vh',
        }}
      >
        <div
          style={{
            maxWidth: '1000px',
            width: '90%',
            margin: `${defaultTheme.spacing.xl} auto`,
            backgroundColor: colors.primaryBg,
            border: `2px solid ${colors.primaryBorder}`,
            borderRadius: '10px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
          }}
        >
          {/* Chat Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: colors.secondaryBg,
              borderBottom: `1px solid ${colors.primaryBorder}`,
              padding: defaultTheme.spacing.md,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: defaultTheme.spacing.sm,
              }}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    backgroundColor: colors.windowControlBg,
                    border: `1px solid ${colors.windowBorder}`,
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: defaultTheme.fontSize.md,
                  fontWeight: '500',
                  color: colors.primaryText,
                }}
              >
                Theme Preview 👀
              </span>
            </div>
          </div>

          {/* Messages Area */}
          <div
            style={{
              padding: '12px',
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
          >
            {/* Welcome Message */}

            <MessageBubble sender="assistant" timestamp="just now">
              <div>
                Hey! 👋 I'm excited to show you{' '}
                <strong>{themeSnapshot.name}</strong>! ✨
              </div>
              <br />
              {themeSnapshot.description && (
                <>
                  <div>{themeSnapshot.description} 🎨</div>
                  <br />
                </>
              )}
              <div
                style={{
                  fontSize: defaultTheme.fontSize.sm,
                  color: colors.tertiaryText,
                }}
              >
                📊 Shared{' '}
                {new Date(themeSnapshot.createdAt).toLocaleDateString()} • 👀{' '}
                {themeSnapshot.viewCount} views • 💾 {themeSnapshot.importCount}{' '}
                imports
              </div>
            </MessageBubble>

            <MessageBubble sender="user" timestamp="just now">
              nice! what does it look like? 🤔
            </MessageBubble>

            <MessageBubble sender="assistant" timestamp="just now">
              <div>
                Great question! 🎯 This page is actually showing you the theme
                RIGHT NOW in real-time! 🪄
              </div>
              <br />
              <div>
                All the colors you're seeing - the backgrounds, text, borders,
                accents - are coming from this theme! Pretty cool, right? 😎
              </div>
            </MessageBubble>

            <MessageBubble sender="user" timestamp="just now">
              love it! how do i get it? 💖
            </MessageBubble>

            <MessageBubble sender="assistant" timestamp="just now">
              <div>Awesome! 🎉 You have two options:</div>
              <br />
              {importSuccess ? (
                <div
                  style={{
                    padding: defaultTheme.spacing.md,
                    backgroundColor: colors.successBg || colors.secondaryBg,
                    border: `1px solid ${colors.successText || colors.primaryAccent}`,
                    borderRadius: defaultTheme.borderRadius.sm,
                    color: colors.successText || colors.primaryText,
                    textAlign: 'center',
                  }}
                >
                  ✅ Imported successfully! Redirecting you now... 🎊
                </div>
              ) : user ? (
                <div
                  style={{
                    display: 'flex',
                    gap: defaultTheme.spacing.sm,
                    marginTop: defaultTheme.spacing.sm,
                  }}
                >
                  <Button
                    onClick={() => handleImport(false)}
                    disabled={importing}
                    isLoading={importing}
                    style={{
                      backgroundColor: colors.secondaryBg,
                      color: colors.primaryText,
                      border: `1px solid ${colors.primaryBorder}`,
                      fontSize: defaultTheme.fontSize.md,
                      padding: `${defaultTheme.spacing.md} ${defaultTheme.spacing.lg}`,
                      flex: 1,
                      fontWeight: '600',
                    }}
                  >
                    💾 Import to My Themes
                  </Button>
                  <Button
                    onClick={() => handleImport(true)}
                    disabled={importing}
                    isLoading={importing}
                    style={{
                      backgroundColor: colors.primaryAccent,
                      color: colors.folderSelectedText,
                      fontSize: defaultTheme.fontSize.md,
                      padding: `${defaultTheme.spacing.md} ${defaultTheme.spacing.lg}`,
                      flex: 1,
                      fontWeight: '600',
                    }}
                  >
                    ✨ Start Using Now
                  </Button>
                </div>
              ) : (
                <>
                  <Button
                    onClick={handleLoginClick}
                    style={{
                      backgroundColor: colors.primaryAccent,
                      color: colors.folderSelectedText,
                      fontSize: defaultTheme.fontSize.md,
                      padding: `${defaultTheme.spacing.md} ${defaultTheme.spacing.xl}`,
                      width: '100%',
                      marginTop: defaultTheme.spacing.sm,
                      fontWeight: '600',
                    }}
                  >
                    🔐 Sign in to Import
                  </Button>
                  <div
                    style={{
                      marginTop: defaultTheme.spacing.sm,
                      fontSize: defaultTheme.fontSize.sm,
                      color: colors.tertiaryText,
                      textAlign: 'center',
                    }}
                  >
                    (You'll need to sign in first! 👤)
                  </div>
                </>
              )}
            </MessageBubble>

            <MessageBubble sender="user" timestamp="just now">
              can i see the raw config? 🤓
            </MessageBubble>

            <MessageBubble sender="assistant" timestamp="just now">
              <div>Of course! For you theme nerds out there 🤓💻</div>
              <br />
              <Button
                onClick={() => setShowJson(!showJson)}
                style={{
                  fontSize: defaultTheme.fontSize.sm,
                  padding: `${defaultTheme.spacing.sm} ${defaultTheme.spacing.md}`,
                  backgroundColor: colors.secondaryBg,
                  color: colors.secondaryText,
                  border: `1px solid ${colors.primaryBorder}`,
                }}
              >
                {showJson ? '🙈 Hide JSON' : '👀 Show JSON'}
              </Button>
              {showJson && (
                <div
                  style={{
                    marginTop: defaultTheme.spacing.md,
                    padding: defaultTheme.spacing.md,
                    backgroundColor: colors.secondaryBg,
                    border: `1px solid ${colors.primaryBorder}`,
                    borderRadius: '10px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontSize: defaultTheme.fontSize.xs,
                      color: colors.primaryText,
                      fontFamily: defaultTheme.fontFamily.mono,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {JSON.stringify(themeData, null, 2)}
                  </pre>
                </div>
              )}
            </MessageBubble>
          </div>

          {/* Chat Input (non-functional, just for aesthetics) */}
          <div
            style={{
              borderTop: `1px solid ${colors.primaryBorder}`,
              padding: defaultTheme.spacing.md,
              backgroundColor: colors.secondaryBg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: defaultTheme.spacing.sm,
                padding: defaultTheme.spacing.sm,
                backgroundColor: colors.primaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: defaultTheme.borderRadius.md,
              }}
            >
              <span
                style={{
                  color: colors.primaryAccent,
                  fontSize: defaultTheme.fontSize.md,
                }}
              >
                {'>'}
              </span>
              <input
                type="text"
                placeholder="Type or speak your message"
                disabled
                style={{
                  flex: 1,
                  backgroundColor: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: colors.tertiaryText,
                  fontFamily: 'var(--font-departure-mono)',
                  fontSize: defaultTheme.fontSize.md,
                }}
              />
              <button
                disabled
                style={{
                  padding: `${defaultTheme.spacing.xs} ${defaultTheme.spacing.md}`,
                  backgroundColor: colors.primaryAccent,
                  color: colors.primaryBg,
                  border: 'none',
                  borderRadius: defaultTheme.borderRadius.sm,
                  fontSize: defaultTheme.fontSize.sm,
                  fontWeight: '600',
                  cursor: 'not-allowed',
                  opacity: 0.6,
                }}
              >
                SEND
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
